import { Dispatch, FC, SetStateAction, useState, useEffect } from "react";
import {
  ArrowLongLeftIcon,
  ArrowLongRightIcon,
} from "@heroicons/react/20/solid";
import { date } from "yup";
import { useNavigate } from "react-location";
import { useUrlState } from "../../utils";

interface Props {
  limit: number;
  skip: number;
  data: {
    rows: any;
    total: number;
  };
  height?: string;
  refetch: () => void;
  setSkip: Dispatch<SetStateAction<number>>;
  setLimit?: Dispatch<SetStateAction<number>>;
}

const TableFooterComponent: FC<Props> = ({
  limit,
  data: { total: dat },
  setSkip,
  skip,
  height,
  refetch,
  setLimit,
}) => {
  const navigate = useNavigate();
  const [page, setPage] = useUrlState("page");
  const [enteredPage, setEnteredPage] = useState(1);

  useEffect(() => {
    const page1 = Math.max(1, parseInt(page || "1", 10));

    if (page1) {
      let pageNumber = page1;
      setPage(pageNumber);
      setEnteredPage(pageNumber); //set entered page
      setSkip((pageNumber - 1) * limit);
    } else {
      setPage(1); //set page
      setEnteredPage(1); //set entered page
      setSkip(0); // set skip
    }
  }, [page, limit, setSkip]);

  const totalPages = Math.ceil(dat / limit);
  const maxPageNumbers = 5; // Number of page numbers to show before/after current page
  const pageNumbers = Array.from(
    { length: totalPages },
    (_, index) => index + 1
  );

  const renderPageNumbers = () => {
    if (totalPages <= maxPageNumbers) {
      return pageNumbers.map((pageNumber) => renderPageLink(pageNumber));
    }

    const pagesBeforeCurrent = Math.floor((maxPageNumbers - 1) / 2);
    const pagesAfterCurrent = maxPageNumbers - 1 - pagesBeforeCurrent;
    const startIndex = Math.max(page - pagesBeforeCurrent - 1, 0);
    const endIndex = Math.min(page + pagesAfterCurrent, totalPages - 1);

    const pages = [];

    if (startIndex > 0) {
      pages.push(renderPageLink(1));
      if (startIndex > 1) {
        pages.push(
          <span
            className="inline-flex items-center mt-4 py-2  border-transparent px-4  text-sm font-medium text-gray-500"
            key="ellipsis-start"
          >
            ...
          </span>
        );
      }
    }

    for (let i = startIndex; i <= endIndex; i++) {
      pages.push(renderPageLink(i + 1));
    }

    if (endIndex < totalPages - 1) {
      if (endIndex < totalPages - 2) {
        pages.push(
          <span
            className="inline-flex items-center mt-4 py-2  border-transparent px-4  text-sm font-medium text-gray-500"
            key="ellipsis-end"
          >
            ...
          </span>
        );
      }
      pages.push(renderPageLink(totalPages));
    }

    return pages;
  };

  const renderPageLink = (pageNumber: number) => (
    <div
      key={pageNumber}
      onClick={() => {
        setPage(pageNumber);
      }}
      className={`inline-flex cursor-pointer rounded-md items-center mt-4 py-2 hover:bg-gray-100 hover:rounded-md border-transparent px-4 text-sm font-medium text-gray-500 ${
        page === pageNumber
          ? "bg-gray-200"
          : "hover:border-gray-300 hover:text-gray-700"
      }`}
      aria-current={page === pageNumber ? "page" : undefined}
    >
      {pageNumber}
    </div>
  );

  return (
    <nav
      className={`flex items-center bg-gray-50 rounded-md h-16 border border-gray-200 rounded-t-none px-4 pb-5 sm:px-0`}
    >
      <div className="flex w-0 flex-1 justify-start">
        <div
          onClick={() => {
            setPage(page - 1);
          }}
          className={`flex items-center cursor-pointer flex-row-reverse hover:bg-gray-100 border mx-3 mt-4 rounded-md pr-3 py-1 border-gray-400 ${
            page === 1
              ? "opacity-50 cursor-not-allowed pointer-events-none"
              : "hover:border-gray-300 hover:text-gray-700"
          } `}
        >
          <p
            className={`inline-flex rounded-md items-center pl-1 text-sm font-medium text-gray-500`}
          >
            Previous
          </p>
          <ArrowLongLeftIcon
            className="ml-3 h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        </div>
      </div>
      <div className="hidden md:flex md:gap-x-1">{renderPageNumbers()}</div>
      <div className="flex w-0 flex-1 justify-end">
        <div
          onClick={() => {
            setPage(page + 1);
          }}
          className={`flex items-center cursor-pointer border mr-3 mt-4 rounded-md hover:bg-gray-100 px-2 py-1 border-gray-400 ${
            page === totalPages
              ? "opacity-50 cursor-not-allowed pointer-events-none"
              : "hover:border-gray-300 hover:text-gray-700"
          }`}
        >
          <p
            className={`inline-flex rounded-md items-center pl-1 text-sm font-medium text-gray-500`}
          >
            Next
          </p>
          <ArrowLongRightIcon
            className="ml-3 h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        </div>
      </div>
    </nav>
  );
};

export default TableFooterComponent;
